import { useEffect, useRef, type ComponentProps } from "react";

import { cx } from "./cx";

export type LevelVariant = "take" | "field";

export interface LevelBarsProps extends Omit<ComponentProps<"div">, "children"> {
  /**
   * The loudest sample since the last call, 0..1 -- `Recorder.takePeak()`. Reset-on-read,
   * so there is exactly one reader and this component is it. Optional: an idle array has
   * nothing to read, and its floors come from the bar table alone.
   */
  read?: () => number;
  /** Off drops every bar to its floor; it does not unmount them. */
  active: boolean;
  /**
   * Which instrument this is.
   *
   * `take` (default) is the enrolment stage's compact mark: fifteen pill bars that sit
   * inside a staged card beside a countdown.
   *
   * `field` is the session screen's wide spectrum: thirty-two thin needles on a taller
   * track, the array a recording feels like from across a table, three-toned so the eye
   * reads loudness as colour before it reads it as height.
   */
  variant?: LevelVariant;
}

/**
 * Each bar's character, derived from its index rather than tuned by hand: a scale (how
 * much of the peak it claims), a decay (how fast it falls back), and a floor (where it
 * idles). All three read the SAME real peak -- the spread is presentation, so the array
 * dances like speech without a single invented sample. Index-derived, so any count works.
 */
function barAt(i: number) {
  return {
    scale: 0.9 + ((i * 37) % 80) / 100,
    decay: 0.74 + ((i * 53) % 16) / 100,
    floor: 0.06 + ((i * 29) % 10) / 100,
  };
}

/**
 * Speech rarely peaks near 1.0 -- the same fact that makes the horizontal meter apply
 * `SCALE` 140 -- and phone microphones sit quieter still. At the meter's 1.4 the array
 * read as dead on a handset, which is the one thing a proof-of-life must not do, so the
 * bars take double gain and reserve their hot colour for near-full-scale only.
 */
const GAIN = 2.2;

/**
 * The two looks, complete: bar count and the classes each bar wears. The container's
 * height is deliberately absent -- the caller sizes the track (`h-16` on the take stage,
 * `h-24` in the session's field), because a shared default a caller overrides is decided
 * by Tailwind's emit order rather than by the caller.
 *
 * The field's base bar is the accent at 40% -- quiet by class -- and the paint loop lifts
 * a reading through the palette as it climbs: full accent past 60%, then the warning amber
 * past 85%, which is the clip zone `--warn` exists for (the horizontal meter's red zone).
 * The take keeps its own rule: accent throughout, danger red only above 95%, because there
 * the bars judge the TAKE -- a clipped take fails -- while the field judges the ROOM.
 */
const LOOKS: Record<LevelVariant, { count: number; gap: string; bar: string }> = {
  take: { count: 15, gap: "gap-1", bar: "w-2 rounded-pill bg-accent" },
  field: { count: 32, gap: "gap-hair", bar: "w-1.5 rounded-t bg-accent/40" },
};

/** The colour a lit bar takes at height `v`, or "" to fall back to the bar's class. */
function toneFor(variant: LevelVariant, v: number): string {
  if (variant === "field") {
    if (v > 0.85) return "var(--warn)";
    if (v > 0.6) return "var(--accent)";
    return "";
  }
  return v > 0.95 ? "var(--danger)" : "";
}

/**
 * The level meter as an equalizer: the same proof-of-life the horizontal meter gives,
 * drawn the way a recording feels -- a field of bars rising with the voice.
 *
 * One reading, many bars: every bar scales and decays the shared peak differently, so
 * the array moves as speech does rather than as copies of one bar.
 *
 * On `requestAnimationFrame` for the same reason the meter is: rise at once, fall by a
 * fixed fraction per frame. No CSS transition smooths the heights -- an interpolation
 * under a per-frame writer would only make the array lag the voice. Deliberately not
 * covered by `prefers-reduced-motion` either: this is not decoration, it is the reading.
 *
 * The cleanup cancels *and* settles: a frozen array on a screen with no microphone open
 * is the one thing this component must never show.
 */
export function LevelBars({ read, active, variant = "take", className, ...rest }: LevelBarsProps) {
  const refs = useRef<Array<HTMLSpanElement | null>>([]);
  // The callback lives in a ref so a re-render with a fresh closure does not tear down
  // and restart the loop. Same contract as the meter's.
  const readRef = useRef(read ?? (() => 0));
  useEffect(() => {
    readRef.current = read ?? readRef.current;
  });

  const { count, gap, bar } = LOOKS[variant];

  useEffect(() => {
    const els = refs.current;
    const settle = () => {
      els.forEach((el, i) => {
        if (!el) return;
        el.style.height = `${Math.round(barAt(i).floor * 100)}%`;
        el.style.backgroundColor = "";
      });
    };
    if (!active) {
      settle();
      return;
    }
    let raf = 0;
    const shown = new Array<number>(count).fill(0);
    const paint = () => {
      const peak = readRef.current();
      for (let i = 0; i < count; i++) {
        const spec = barAt(i);
        const v = Math.min(1, peak * GAIN * spec.scale);
        shown[i] = v > shown[i] ? v : Math.max(spec.floor, shown[i] * spec.decay);
        const el = els[i];
        if (el) {
          el.style.height = `${Math.round(shown[i] * 100)}%`;
          el.style.backgroundColor = toneFor(variant, shown[i]);
        }
      }
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    return () => {
      cancelAnimationFrame(raf);
      settle();
    };
  }, [active, variant, count]);

  return (
    <div
      className={cx("w-full flex items-end justify-center", gap, className)}
      aria-hidden="true"
      {...rest}
    >
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className={bar}
          style={{ height: `${Math.round(barAt(i).floor * 100)}%` }}
        />
      ))}
    </div>
  );
}
